/**
 * Stepper und Aktionskarte der Vorgangsseite („Salon“, Antoine 26.09.2026).
 *
 * - Stepper: erledigte Schritte orange mit Haken, der aktuelle leuchtet („jetzt“), kommende sind
 *   gestrichelt. Am Handy wird daraus „Schritt 2 von 6 · Entwurf“ mit einer Balkenreihe.
 * - Aktionskarte: warm getönt, darin der nächste Schritt und genau EIN oranger Knopf je Status
 *   (`primaer`). Am Handy steht dieser Knopf fest unten über der Navigation — dasselbe Element,
 *   nur per CSS angeheftet, damit es kein zweites Mal im Dokument steht. Leise Nebenaktionen
 *   (`leise`) bleiben in der Karte.
 */
import type { ReactNode } from "react";
import { Icon } from "../components";
import { GRUEN_FUELLUNG } from "../../lib/zahlungsstand";

export interface Schritt {
  key: string;
  label: string;
}

export function VorgangSchritte({
  schritte,
  aktuell,
  ausserhalb,
  zahlung,
}: {
  schritte: Schritt[];
  /** Index des aktuellen Schritts; -1 = Status liegt außerhalb der Kette (z. B. „Keine Antwort“). */
  aktuell: number;
  /** Beschriftung des Status, wenn er außerhalb der Kette liegt. */
  ausserhalb?: string;
  /** Zahlungsstand am Schritt `key`: „halb“ = Anzahlung eingegangen (50 % orange), „gruen“ = gedeckt. */
  zahlung?: { key: string; art: "halb" | "gruen" } | null;
}) {
  return (
    <>
      {/* Handy: kompakt */}
      <div className="flex flex-col gap-2 sm:hidden" data-testid="stepper-kompakt">
        <p className="text-[10.5px] font-extrabold uppercase tracking-[0.1em] text-primary">
          {aktuell >= 0 ? `Schritt ${aktuell + 1} von ${schritte.length} · ${schritte[aktuell]?.label ?? ""}` : `Status · ${ausserhalb ?? ""}`}
        </p>
        <div className="flex gap-1" aria-hidden>
          {schritte.map((s, i) => (
            <i
              key={s.key}
              data-zahlung={zahlung?.key === s.key ? zahlung.art : undefined}
              className={`h-1.5 flex-1 rounded-full ${
                zahlung?.key === s.key && zahlung.art === "gruen"
                  ? GRUEN_FUELLUNG
                  : zahlung?.key === s.key && zahlung.art === "halb" && i > aktuell
                    ? "bg-gradient-to-r from-primary from-50% to-surface-container-highest to-50% dark:to-white/[0.14]"
                    : i < aktuell ? "bg-primary" : i === aktuell ? "bg-inverse-primary" : "bg-surface-container-highest dark:bg-white/[0.14]"
              }`}
            />
          ))}
        </div>
      </div>
      {/* ab sm: volle Kette */}
      <ol className="hidden items-start sm:flex" aria-label="Fortschritt" data-testid="stepper">
        {schritte.map((s, i) => {
          const fertig = i < aktuell;
          const jetzt = i === aktuell;
          const zahlungHier = zahlung?.key === s.key ? zahlung.art : null;
          const halb = zahlungHier === "halb" && !fertig && !jetzt;
          const gruen = zahlungHier === "gruen";
          return (
            <li key={s.key} className="flex flex-1 items-start last:flex-none" aria-current={jetzt ? "step" : undefined}>
              <div className="flex w-[84px] shrink-0 flex-col items-center gap-2 text-center lg:w-[96px]">
                <span
                  data-zahlung={zahlungHier ?? undefined}
                  className={`grid h-[30px] w-[30px] place-items-center rounded-full text-[12px] font-extrabold ${
                    gruen
                      ? `${GRUEN_FUELLUNG} text-white dark:text-[#17130E]`
                      : halb
                        ? "border-[1.5px] border-primary bg-[linear-gradient(90deg,#C2410C_50%,transparent_50%)] text-secondary-fixed-dim"
                        : fertig
                      ? "bg-primary text-on-primary shadow-[0_2px_6px_rgba(194,65,12,0.35)]"
                      : jetzt
                        ? "border-[2.5px] border-primary bg-surface-container-lowest shadow-[0_0_0_6px_rgba(194,65,12,0.14)] dark:bg-night-card"
                        : "border-[1.5px] border-dashed border-outline bg-surface-container-low text-secondary-fixed-dim dark:border-white/25 dark:bg-white/[0.04]"
                  }`}
                >
                  {fertig || gruen ? <Icon name="check" className="text-[17px]" /> : jetzt ? <span className="h-2.5 w-2.5 rounded-full bg-primary" /> : i + 1}
                </span>
                <span
                  className={`text-[12px] leading-tight ${
                    jetzt ? "font-bold text-primary" : fertig ? "font-bold text-on-background dark:text-inverse-on-surface" : "font-semibold text-secondary-fixed-dim"
                  }`}
                >
                  {s.label}
                </span>
                {jetzt && <span className="-mt-1 text-[11px] font-bold text-primary">jetzt</span>}
              </div>
              {i < schritte.length - 1 && (
                <span aria-hidden className={`mt-[13px] h-1 flex-1 rounded-full ${fertig ? "bg-primary" : "bg-surface-container-highest dark:bg-white/[0.14]"}`} />
              )}
            </li>
          );
        })}
      </ol>
    </>
  );
}

export interface CheckPunkt {
  text: string;
  erledigt: boolean;
}

export function AktionsKarte({
  icon,
  titel,
  zeile,
  checkliste,
  primaer,
  leise,
  zusatz,
  ton = "primaer",
  testId,
}: {
  icon: string;
  titel: string;
  zeile?: ReactNode;
  checkliste?: CheckPunkt[];
  /** Der EINE orange Knopf. Am Handy fest unten angeheftet. */
  primaer?: ReactNode;
  /** Nebenaktionen, leise daneben. */
  leise?: ReactNode;
  /** Eigene Zeile unter Text und Knöpfen über die ganze Breite (z. B. OpenTable). */
  zusatz?: ReactNode;
  /** `ruhig` für Status ohne Handlungsbedarf, `gefahr` für eine Absage mit Zahlung. */
  ton?: "primaer" | "ruhig" | "gefahr";
  testId?: string;
}) {
  const huelle =
    ton === "gefahr"
      ? "border-error/40 bg-error-container/40 dark:bg-error/10"
      : ton === "ruhig"
        ? "border-outline-variant/60 bg-surface-container-low dark:border-white/10 dark:bg-white/[0.03]"
        : "border-primary-fixed-dim bg-primary-container/40 dark:border-primary/40 dark:bg-primary/[0.12]";
  const kachel =
    ton === "gefahr"
      ? "bg-error text-on-error"
      : ton === "ruhig"
        ? "bg-surface-container-high text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim"
        : "bg-primary text-on-primary shadow-[0_6px_14px_-4px_rgba(194,65,12,0.5)]";
  return (
    <div className={`flex flex-wrap items-center gap-x-5 gap-y-4 rounded-2xl border p-4 sm:p-5 lg:px-6 ${huelle}`} data-testid={testId}>
      <span className={`hidden h-[52px] w-[52px] shrink-0 place-items-center rounded-[14px] sm:grid ${kachel}`}>
        <Icon name={icon} className="text-[26px]" />
      </span>
      <div className="min-w-0 flex-1 basis-[240px]">
        <p className={`text-[10.5px] font-extrabold uppercase tracking-[0.1em] ${ton === "gefahr" ? "text-error" : "text-primary"}`}>Nächster Schritt</p>
        <p className="mt-1 font-serif text-[20px] font-medium leading-[1.2] text-on-background dark:text-inverse-on-surface sm:text-[24px]">{titel}</p>
        {zeile && <div className="mt-1.5 text-[12.5px] text-secondary dark:text-secondary-fixed-dim">{zeile}</div>}
        {checkliste && checkliste.length > 0 && (
          <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 text-[12.5px] text-secondary dark:text-secondary-fixed-dim" aria-label="Was noch fehlt">
            {checkliste.map((c) => (
              <li key={c.text} className="inline-flex items-center gap-1.5">
                <Icon
                  name={c.erledigt ? "check_circle" : "radio_button_unchecked"}
                  className={`text-[16px] ${c.erledigt ? "text-tertiary dark:text-[#b8ae9e]" : "text-[#6F675A] dark:text-[#b8ae9e]"}`}
                />
                {c.text}
              </li>
            ))}
          </ul>
        )}
      </div>
      {(primaer || leise) && (
        <div className="flex w-full flex-col items-stretch gap-2 max-lg:contents sm:w-auto">
          {primaer && (
            // Handy: fest über der unteren Navigation (ca. 58 px + Safe-Area), ab lg normal in der Karte.
            <div
              data-testid="hauptaktion"
              className="fixed inset-x-0 bottom-[calc(3.7rem+env(safe-area-inset-bottom))] z-30 flex border-t border-surface-variant bg-surface-container-lowest px-3.5 py-2.5 shadow-[0_-12px_24px_-16px_rgba(45,38,26,0.35)] dark:border-white/10 dark:bg-night-card lg:static lg:z-auto lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none lg:dark:bg-transparent [&>*]:flex-1 lg:[&>*]:flex-none"
            >
              {primaer}
            </div>
          )}
          {leise && <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:justify-end">{leise}</div>}
        </div>
      )}
      {zusatz}
    </div>
  );
}
